/**
 * AdminPendingFallback — pending fallback for the _admin route.
 *
 * Renders an admin-shaped skeleton (thin header bar + 3-row content skeleton) during
 * the _admin beforeLoad (silent refresh, slow network). Switches to a bilingual
 * recovery UI after 8s to surface hung-silent-refresh failure mode.
 *
 * Deviation from plan: plan imports `clearAccessToken` which is not exported from
 * @repo/api-client. Using `setAccessToken(null)` + `setTokenExpiry(null)` instead,
 * consistent with the existing setUnauthorizedHandler in main.tsx.
 */

import { setAccessToken, setTokenExpiry } from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { Skeleton } from '@repo/ui/components/skeleton';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { clearUser } from '../../lib/auth';
import { BilingualErrorCard } from './BilingualErrorCard';

const PENDING_TIMEOUT_MS = 8000;

export function AdminPendingFallback() {
  const [tookTooLong, setTookTooLong] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const handle = setTimeout(() => setTookTooLong(true), PENDING_TIMEOUT_MS);
    return () => clearTimeout(handle); // critical: clean up on resolve
  }, []);

  if (tookTooLong) {
    return (
      <BilingualErrorCard
        role="status"
        titleBn="অনেক সময় লাগছে"
        titleEn="TAKING LONGER THAN USUAL"
        bodyBn="সার্ভার থেকে উত্তর পেতে দেরি হচ্ছে। নেটওয়ার্ক বা সার্ভার সমস্যা হতে পারে।"
        bodyEn="The server is taking longer than usual to respond. This may be a network issue or a server outage."
        actions={
          <div className="flex gap-3">
            <Button
              onClick={() => router.invalidate()}
              className="bg-red text-red-ink hover:bg-red/90 min-h-[44px] px-6"
            >
              আবার চেষ্টা / RETRY
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setAccessToken(null);
                setTokenExpiry(null);
                clearUser();
                router.navigate({ to: '/login', search: { redirect: undefined } });
              }}
              className="min-h-[44px] px-6"
            >
              লগআউট / LOG OUT
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="অ্যাডমিন প্যানেল লোড হচ্ছে / Loading admin panel"
      className="flex flex-col gap-4"
    >
      <Skeleton className="h-12 w-full rounded-none" /> {/* header bar */}
      <div className="flex flex-col gap-4 px-6 pt-4">
        <Skeleton className="h-8 w-48 rounded-sm" />
        <Skeleton className="h-4 w-72 rounded-sm" />
        <Skeleton className="h-64 w-full rounded-sm" />
      </div>
    </div>
  );
}
